import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import { MapPin, Mountain, ChevronLeft, Trash2, Share2 } from "lucide-react";
import { toast } from "sonner";
import ShareModal from "../components/ShareModal";

export default function MarketplaceDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [listing, setListing] = useState(null);
    const [active, setActive] = useState(0);
    const [loading, setLoading] = useState(true);
    const [shareOpen, setShareOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/marketplace/${id}`);
                if (!cancelled) setListing(data);
            } catch {
                toast.error("Listing not found");
                navigate("/marketplace");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id, navigate]);

    const handleDelete = async () => {
        if (!window.confirm("Delete this listing?")) return;
        try {
            await api.delete(`/marketplace/${id}`);
            toast.success("Deleted");
            navigate("/marketplace");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading || !listing) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-ember-orange border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const isOwner = user?.user_id === listing.seller?.user_id;
    const images = listing.images || [];

    return (
        <div>
            <TopBar title="Listing" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <button
                    onClick={() => navigate("/marketplace")}
                    data-testid="listing-back-btn"
                    className="inline-flex items-center gap-1 text-sm font-bold text-midnight-pine mb-6 hover:text-ember-orange"
                >
                    <ChevronLeft className="w-4 h-4" /> Back to marketplace
                </button>

                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                    <div>
                        <div className="aspect-square rounded-lg overflow-hidden bg-alpine-mist border border-ridge-line mb-3">
                            {images[active] && <img src={images[active]} alt={listing.title} className="w-full h-full object-cover" />}
                        </div>
                        {images.length > 1 && (
                            <div className="grid grid-cols-5 gap-2">
                                {images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActive(i)}
                                        data-testid={`listing-thumb-${i}`}
                                        className={`aspect-square rounded-md overflow-hidden border-2 ${
                                            i === active ? "border-ember-orange" : "border-transparent hover:border-ridge-line"
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-trail-tan text-midnight-pine capitalize">
                                {listing.category}
                            </span>
                            <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-moss/20 text-moss capitalize">
                                {listing.condition}
                            </span>
                        </div>
                        <h1 className="font-display font-black text-3xl lg:text-4xl text-midnight-pine tracking-tight leading-tight mb-3">
                            {listing.title}
                        </h1>
                        <div className="font-display font-black text-5xl text-ember-orange mb-5">
                            ${listing.price}
                        </div>
                        {listing.location && (
                            <div className="flex items-center gap-1 text-sm text-slate-gray mb-6">
                                <MapPin className="w-4 h-4" /> {listing.location}
                            </div>
                        )}
                        <p className="text-granite leading-relaxed mb-8 whitespace-pre-line">{listing.description}</p>

                        <div className="bg-snowcap border border-ridge-line rounded-lg p-5 mb-5">
                            <div className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-3">Seller</div>
                            <Link
                                to={`/profile/${listing.seller?.user_id}`}
                                data-testid="listing-seller-link"
                                className="flex items-center gap-3 group"
                            >
                                <div className="w-12 h-12 rounded-full overflow-hidden bg-trail-tan">
                                    {listing.seller?.avatar ? (
                                        <img src={listing.seller.avatar} alt={listing.seller.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <Mountain className="w-5 h-5 text-midnight-pine m-auto mt-3.5" />
                                    )}
                                </div>
                                <div>
                                    <div className="font-bold text-granite group-hover:text-ember-orange transition-colors">
                                        {listing.seller?.name}
                                    </div>
                                    <div className="text-xs text-slate-gray">View profile →</div>
                                </div>
                            </Link>
                        </div>

                        <div className="flex gap-3">
                            <button
                                data-testid="listing-contact-btn"
                                onClick={() => toast.info("Messaging coming soon")}
                                className="flex-1 py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.99] transition-all"
                            >
                                Contact seller
                            </button>
                            <button
                                data-testid="listing-share-btn"
                                onClick={() => setShareOpen(true)}
                                className="px-4 py-3 rounded-md border border-ridge-line text-granite font-bold hover:border-midnight-pine transition-all inline-flex items-center gap-2"
                            >
                                <Share2 className="w-4 h-4" /> Share
                            </button>
                            {isOwner && (
                                <button
                                    onClick={handleDelete}
                                    data-testid="listing-delete-btn"
                                    className="px-4 py-3 rounded-md border border-trail-red/50 text-trail-red hover:bg-trail-red hover:text-white transition-all"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <ShareModal
                open={shareOpen}
                onOpenChange={setShareOpen}
                kind="marketplace"
                id={listing.id}
                title={listing.title}
                byline={`— $${listing.price}`}
                preview={[
                    `$${listing.price}`,
                    listing.condition,
                    listing.category,
                    listing.location,
                ]
                    .filter(Boolean)
                    .join(" · ")}
            />
        </div>
    );
}
